import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  OptionsFilter,
} from '@mantine/core';
import { MemberOptionRow } from '@/components/common/MemberOptionRow';
import { getMemberLabel } from '@/utils';
import type { WorkspaceMember } from '@/types/workspace';

interface UseMemberComboboxOptionsReturn {
  readonly options: ComboboxItem[];
  readonly renderOption: (
    input: ComboboxLikeRenderOptionInput<ComboboxItem>
  ) => ReactNode;
  readonly filter: OptionsFilter;
}

/**
 * Shared member→combobox-option derivation used by the single-select
 * MemberSelectView and the multi-select MemberFilterMultiSelect: builds
 * {value:userId,label} options, an avatar+name renderOption (MemberOptionRow),
 * and an email-aware OptionsFilter, keeping both pickers' option presentation
 * and search behaviour in lockstep.
 */
export function useMemberComboboxOptions(
  members: readonly WorkspaceMember[]
): UseMemberComboboxOptionsReturn {
  const options = useMemo<ComboboxItem[]>(
    () =>
      members.map((member) => ({
        value: member.userId,
        label: getMemberLabel(member),
      })),
    [members]
  );

  const memberMap = useMemo(
    () => new Map(members.map((member) => [member.userId, member])),
    [members]
  );

  const renderOption = useCallback(
    ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>): ReactNode => {
      const member = memberMap.get(option.value);
      if (!member) return option.label;
      return <MemberOptionRow member={member} />;
    },
    [memberMap]
  );

  // Mantine's default filter only matches the option `label` (display name), so
  // typing part of an email finds nothing. This custom filter also matches the
  // member's email (case-insensitive substring). The label already equals the
  // displayName when present, so it never needs to be searched separately.
  const filter = useMemo<OptionsFilter>(
    () =>
      ({ options: filterOptions, search }) => {
        const normalizedSearch = search.trim().toLowerCase();
        if (normalizedSearch.length === 0) return filterOptions;

        return filterOptions.filter((option) => {
          // The member data is a flat ComboboxItem[], so groups never occur;
          // skip any group entry defensively to satisfy the parsed-item union.
          if (!('label' in option)) return false;

          const member = memberMap.get(option.value);
          const haystacks = [option.label, member?.email];

          return haystacks.some((candidate) =>
            candidate?.toLowerCase().includes(normalizedSearch)
          );
        });
      },
    [memberMap]
  );

  return { options, renderOption, filter };
}
